

import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { namespace, Getter, Action, State } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"
import DataTable from "../DataTable.vue"

import User from "@/types/user"
import { TemplateDetails, Field, Template, DataDefinitionWithFields, DataType } from "@/types"
import { CaseField } from "@/types/caseField"

import DataForm from "../DataForm.vue"
import Numeric from "../../Fields/Numeric.vue"
import UserRoleSetup from "./UserRoleSetup.vue"

import api from "@/services/api"
import { CalculationDataTypeValue, CalculationType } from "@/types/field"
import { MarkerColors } from "@/services/mapProvider"

import { CaseColumn, caseColumns } from "@/types/caseColumns"

import { defaultTimeSpentAsValue, TimeSpentAsValue } from "@/types/timeSpentAsValue"

const setup = namespace("setup")

interface CalculationTypeOption {
	key: CalculationType;
	label: string;
}

@Component({
	components: {
		"v-select": vSelect,
		"numeric": Numeric,
		"user-role-setup": UserRoleSetup
	}
})
export default class CalculatedField extends Vue {
	@Prop()
	public item!: Field | null;

	@Prop()
	public value: any;

	@Prop({ default: () => {} })
	public tenantEnabledFeatures!: { [key: string]: boolean }

	@setup.State
	fields!: Field[];

	get allFields (): Field[] {
		return [
			...caseColumns
				.filter(c => c.key !== "lastUpdated")
				.map(c => {
					return {
						id: c.key,
						title: c.label,
						dataType: c.dataType
					} as Field
				}),
			...this.fields
		]
	}

	// Enums used in mark up
	private calculationTypeEnum = CalculationType;

	private itemSourceDefinition: DataDefinitionWithFields | null = null;
	private dataDefinition: DataDefinitionWithFields | null = null;

	private filterFields: Field[] = [];

	private markerColors = MarkerColors;

	private calculationTypes: CalculationTypeOption[] = [
		{ key: CalculationType.Numerical, label: "Numerical" },
		{ key: CalculationType.Total, label: "Total" },
		{ key: CalculationType.Average, label: "Average" },
		{ key: CalculationType.AddToDate, label: "Add to date" },
		{ key: CalculationType.DaysBetweenDates, label: "Days between dates" },
		{ key: CalculationType.DaysUntilOrFromToday, label: "Days until or from today" },
		{ key: CalculationType.GreaterThan, label: "Greater Than" },
		{ key: CalculationType.LessThan, label: "Less Than" },
		{ key: CalculationType.TimeSpentAs, label: "Time Spent As" }
	];

	public get numericFields (): Field[] {
		let numericFields = this.fields.filter(fi => fi.dataType === "number" || fi.dataType === "dollar" ||
			(fi.dataType === "calculated" && fi.id !== this.value.id && fi.extensions && CalculationDataTypeValue[fi.extensions.calculationType as CalculationType] === DataType.number))
		// we also need to add on user-role fields that have time tracking enabled
		numericFields = numericFields.concat(this.fields.filter(fi => fi.dataType === "user-role" && fi.id !== this.value.id &&
		(fi.extensions &&
		(fi.extensions.allowTimeTracking ||
			(fi.extensions.subroles && fi.extensions.subroles.find((x: { allowTimeTracking: boolean; }) => x.allowTimeTracking === true))
		))))

		return numericFields
	}

	public get dateFields (): Field[] {
		return this.allFields.filter(fi => fi.dataType === "date" || fi.dataType === "date-time" ||
			(fi.dataType === "calculated" && fi.extensions &&
				(fi.extensions.calculationType === CalculationType.AddToDate)))
	}

	public get dataListFields (): Field[] {
		return this.fields.filter(f => f.definitionId)
	}

	public get dataSingleListFields (): Field[] {
		// only user defined fields currently supported (this.fields as opposed to this.allFields) as most built in fields do not recalculate calculated fields on change
		return this.fields.filter(f => f.dataType === "list")
	}

	public get dataItemFields (): Field[] {
		if (!this.dataDefinition) {
			return []
		}

		return this.dataDefinition.fields.filter(fi => fi.field.dataType === "number" || fi.field.dataType === "dollar").map(fi => fi.field)
	}

	public get dataSourceDefinitionFields (): Field[] {
		if (!this.itemSourceDefinition) {
			return []
		}

		return this.itemSourceDefinition.fields.map(fi => fi.field)
	}

	public get dataItemFieldsMatchType (): Field[] {
		if (!this.dataDefinition || !this.item) {
			return []
		}

		return this.dataDefinition.fields.filter(fi => fi.field.dataType === this.item!.dataType).map(fi => fi.field)
	}

	public get allowCalculatedOverride (): boolean {
		return this.item && this.item.dataType === "calculated" && this.value && this.value.calculationType === CalculationType.AddToDate
	}

	mounted () {
		if (!this.value) {
			if (this.item && this.item.dataType !== "calculated") {
				this.$emit("input", {
					calculationType: null
				})
			} else {
				this.$emit("input", {
					calculationType: 0,
					fields: []
				})
			}
		} else {
			if (this.item && this.item.dataSourceId) {
				this.dataSourceChanged()
			}

			if (this.value.dataListField) {
				this.getDefinitionDetails(this.value.dataListField).then((definition: DataDefinitionWithFields | null) => {
					this.dataDefinition = definition
				})
			}

			if (this.value.calculationType !== undefined) {
				this.onCalculationTypeChanged(this.value.calculationType)
			}
		}
	}

	@Watch("item.dataSourceId")
	public async dataSourceChanged () {
		if (this.item && this.item.dataSourceId) {
			this.itemSourceDefinition = await api.definitionDetailsForSource(this.item.dataSourceId!)

			if (this.value.listFilter) {
				this.filterListValueSet(this.value.listFilter)
			}
		} else {
			this.itemSourceDefinition = null
		}
	}

	@Watch("value.calculationType")
	public onCalculationTypeChanged (calculationType: number | null) {
		if (calculationType === null) {
			if (!this.value.dateListField) {
				Vue.set(this.value, "dataListField", null)
			}
			if (!this.value.dataItemField) {
				Vue.set(this.value, "dataItemField", null)
			}
		} else if (calculationType === CalculationType.Numerical) {
			if (!this.value.add) {
				Vue.set(this.value, "add", [])
			}
			if (!this.value.subtract) {
				Vue.set(this.value, "subtract", [])
			}
		} else if (calculationType === CalculationType.AddToDate) {
			if (!this.value.dateField) {
				Vue.set(this.value, "dateField", null)
			}
			if (!this.value.add) {
				Vue.set(this.value, "add", [])
			}
			if (!this.value.showTime) {
				Vue.set(this.value, "showTime", true)
			}
		} else if (calculationType === CalculationType.Total || calculationType === CalculationType.Average) {
			if (!this.value.dateListField) {
				Vue.set(this.value, "dataListField", null)
			}
			if (!this.value.dataItemField) {
				Vue.set(this.value, "dataItemField", null)
			}
		} else if (calculationType === CalculationType.DaysBetweenDates) {
			if (!this.value.startField) {
				Vue.set(this.value, "startField", null)
			}
			if (!this.value.endField) {
				Vue.set(this.value, "endField", null)
			}
		} else if (calculationType === CalculationType.DaysUntilOrFromToday) {
			if (!this.value.dateField) {
				Vue.set(this.value, "dateField", null)
			}
		} else if (calculationType === CalculationType.TimeSpentAs) {
			if (!this.value) {
				Vue.set(this.value, "trackingFieldId", "")
			}
		}

		this.$emit("input", this.value)
	}

	public updateValue () {
		this.$emit("input", this.value)
	}

	@Watch("value.dataListField")
	public async listFieldChanged (fieldId: string) {
		Vue.set(this.value, "dataItemField", null)

		if (!fieldId) {
			this.dataDefinition = null
			return
		}

		this.dataDefinition = await this.getDefinitionDetails(fieldId)
	}

	@Watch("item.dataType")
	public async dataTypeChanged () {
		if (this.value.dataItemField) {
			// If the data type has changed, the field it was reading from is probably no longer applicable
			if (!this.dataItemFieldsMatchType.find(f => f.id === this.value.dataItemField)) {
				this.value.dataItemField = null
			}
		}

		if (this.value.allowAddingToDataSource) {
			delete this.value.allowAddingToDataSource
		}
	}

	public async getDefinitionDetails (fieldId: string) {
		const dataListField = this.fields.find(f => f.id === fieldId)
		if (!dataListField || (!dataListField.dataSourceId && !dataListField.definitionId)) {
			return null
		}

		if (dataListField.definitionId) {
			return api.definitionDetails(dataListField.definitionId)
		} else {
			return api.definitionDetailsForSource(dataListField.dataSourceId!)
		}
	}

	@Watch("value.dateField")
	dateTimeFieldChanged (fieldId: string) {
		if (!fieldId) {
			Vue.set(this.value, "showTime", false)
			return
		}

		const dateField = this.allFields.find(f => f.id === fieldId)
		Vue.set(this.value, "showTime", dateField != null && dateField.dataType === "date-time")
	}

	public static stripUnusedOptions (field: Field) {
		if (!field.extensions) {
			return
		}

		if (field.dataType !== "calculated") {
			return
		}

		let allowedExtensions: any = ["calculationType", "fields"]

		switch (field.extensions.calculationType) {
			case CalculationType.Numerical:
				allowedExtensions = allowedExtensions.concat(["add", "subtract", "static"])
				field.extensions.fields = (field.extensions.add as string[]).concat(field.extensions.subtract as string[])

				break
			case CalculationType.Total:
			case CalculationType.Average:
				allowedExtensions = allowedExtensions.concat(["dataListField", "dataItemField", "static"])
				field.extensions.fields = [field.extensions.dataListField]

				break
			case CalculationType.AddToDate:
				allowedExtensions = allowedExtensions.concat(["add", "dateField", "static", "showTime", "allowOverride"])
				field.extensions.fields = [field.extensions.dateField, ...(field.extensions.add ? field.extensions.add as string[] : [])]

				break
			case CalculationType.DaysBetweenDates:
				allowedExtensions = allowedExtensions.concat(["startField", "endField"])
				field.extensions.fields = [field.extensions.startField, field.extensions.endField]

				break
			case CalculationType.DaysUntilOrFromToday:
				allowedExtensions = allowedExtensions.concat(["dateField"])
				field.extensions.fields = [field.extensions.dateField]

				break
			case CalculationType.GreaterThan:
			case CalculationType.LessThan:
				allowedExtensions = allowedExtensions.concat(["checkField", "otherField", "value", "allowEqual"])
				field.extensions.fields = [field.extensions.checkField, field.extensions.otherField]

				break
			case CalculationType.TimeSpentAs:
				allowedExtensions = allowedExtensions.concat(["trackingFieldId", "isClosed", "entries"])
				field.extensions.fields = [field.extensions.trackingFieldId, "isclosed"]

				break
		}

		const extensionFields = Object.keys(field.extensions)
		extensionFields.forEach(key => {
			if (allowedExtensions.indexOf(key) === -1) {
				delete field.extensions![key]
			}
		})
	}

	private filterListValueSet (definitionFieldId: string) {
		if (this.itemSourceDefinition) {
			const selectedValue = this.itemSourceDefinition.fields.find(f => f.field.id === definitionFieldId)

			if (selectedValue) {
				this.filterFields = this.fields.filter(f => f.dataType === selectedValue.field.dataType)

				return
			}
		}

		this.filterFields = []
	}
}

